import yup from '../utils/yup'

/**
 * Runtime configuration.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time. Baking the OIDC authority or the API
 * endpoints into the bundle would tie one build to one environment and break the twelve-factor
 * separation of build, release and run — the container image could no longer be promoted from
 * staging to production unchanged.
 *
 * So the bundle fetches `/config.json` from the Play application that serves it, before any
 * module that reads configuration is evaluated. See `src/main.tsx`. The dev server proxies it to
 * the local Play application, whose defaults are the local values: one source, no untracked
 * `.env` file.
 */

export interface AppRuntimeConfig {
  graphqlEndpoint: string
  orderSystemBaseUrl: string
  oidc: {
    authority: string
    clientId: string
    redirectUri: string
    postLogoutRedirectUri: string
  }
  faroUrl?: string
  faroApiKey?: string
  faroEnvironment?: string
}

export interface FaroConfig {
  url: string
  apiKey: string
  environment?: string
}

const runtimeConfigSchema = yup.object({
  graphqlEndpoint: yup.string().required(),
  orderSystemBaseUrl: yup.string().required(),
  faroUrl: yup.string(),
  faroApiKey: yup.string(),
  faroEnvironment: yup.string(),
  oidc: yup
    .object({
      authority: yup.string().required(),
      clientId: yup.string().required(),
      redirectUri: yup.string().required(),
      postLogoutRedirectUri: yup.string().required(),
    })
    .required(),
})

let runtime: AppRuntimeConfig | undefined

/**
 * Must resolve before any module that reads `appConfig` is evaluated.
 *
 * Deliberately has no fallback to build-time values: silently serving a bundle
 * configured for the wrong realm is worse than failing to start.
 */
export async function loadAppConfig(): Promise<void> {
  const response = await fetch('/config.json', { credentials: 'same-origin' })
  if (!response.ok) {
    throw new Error(`Could not load /config.json: ${response.status} ${response.statusText}`)
  }
  runtime = await runtimeConfigSchema.validate(await response.json())
}

/** Test seam — lets unit tests supply a configuration without a network round trip. */
export function setAppConfigForTest(config: AppRuntimeConfig): void {
  runtime = config
}

function loaded(): AppRuntimeConfig {
  if (!runtime) {
    throw new Error('Application configuration was read before loadAppConfig() resolved')
  }
  return runtime
}

const config = {
  api: {
    get graphQlEndpoint() {
      return loaded().graphqlEndpoint
    },
    get orderSystemBaseUrl() {
      return loaded().orderSystemBaseUrl
    },
  },
  oidc: {
    get authority() {
      return loaded().oidc.authority
    },
    get clientId() {
      return loaded().oidc.clientId
    },
    get redirectUri() {
      return loaded().oidc.redirectUri
    },
    get postLogoutRedirectUri() {
      return loaded().oidc.postLogoutRedirectUri
    },
  },
  /** Absent where no collector is configured, and then the browser reports nothing. */
  get faro(): FaroConfig | undefined {
    const { faroUrl, faroApiKey, faroEnvironment } = loaded()
    if (!faroUrl || !faroApiKey) return undefined
    return { url: faroUrl, apiKey: faroApiKey, environment: faroEnvironment }
  },
  snackbar: {
    autoHideDurationMs: 5000,
  },
}

export default config
